// invest_FE/src/components/verification/AuthenticatorSetup.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import { UserContext } from '../../context/UserContext';
import { NotificationContext } from '../../context/NotificationContext';
import './AuthenticatorSetup.css';

const AuthenticatorSetup = ({ setupToken, onSuccess, onCancel }) => {
  const { setupAuthenticator, verifyAuthenticatorSetup } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);

  const [isLoading, setIsLoading] = useState(true);
  const [setupData, setSetupData] = useState(null); // { secret, qrCodeUrl }
  const [verificationCode, setVerificationCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState('');
  const setupStarted = useRef(false);

  useEffect(() => {
    const startSetup = async () => {
      setIsLoading(true);
      setError('');
      try {
        const data = await setupAuthenticator(setupToken);
        setSetupData(data);
      } catch (err) {
        setError(err.message);
        addNotification(`Error: ${err.message}`, 'error');
      } finally {
        setIsLoading(false);
      }
    };

    // This effect should only run once when the component is mounted with a valid token.
    // The setupStarted ref prevents it from running again on re-renders.
    if (!setupToken) {
      // If the token isn't available on mount, show an error.
      setError('Setup token is missing. Cannot proceed.');
      setIsLoading(false);
    } else if (!setupStarted.current) {
      // If we have a token and haven't started, begin the setup.
      setupStarted.current = true;
      startSetup();
    }
  }, [setupToken, addNotification, setupAuthenticator]);

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    if (!verificationCode || !setupData.secret) {
      setError('Please enter the verification code.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      // This now returns the full login data from the backend
      const loginData = await verifyAuthenticatorSetup({ code: verificationCode, secret: setupData.secret, token: setupToken, trustDevice });
      onSuccess(loginData); // Pass the login data up to the parent component
    } catch (err) {
      setError(err.message);
      addNotification(`Error: ${err.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !setupData) {
    return <div className="authenticator-setup-container">Loading...</div>;
  }

  if (error && !setupData) {
    return (
      <div className="authenticator-setup-container">
        <p className="error-message">{error}</p>
        <button onClick={onCancel} className="btn btn-secondary">
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="authenticator-setup-container">
      <h4>Set Up Authenticator App</h4>

      <div className="setup-content-wrapper">
        <div className="setup-left-column">
          <p>Scan the QR code with your authenticator app (e.g., Google Authenticator), then enter the 6-digit code to verify.</p>
          {setupData?.qrCodeUrl && (
            <div className="qr-code-container">
              <img src={setupData.qrCodeUrl} alt="QR Code for 2FA setup" />
            </div>
          )}
        </div>
        <div className="setup-right-column">
          <form onSubmit={handleVerificationSubmit} className="verification-form">
            <input type="text" value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} onClick={() => setVerificationCode('')} placeholder="123456" maxLength="6" required className="otp-input-field" />
            <div className="trust-device-container">
              <input type="checkbox" id="trust-device-setup" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} />
              <label htmlFor="trust-device-setup">Trust this device for 30 days</label>
            </div>
            {error && <p className="error-message">{error}</p>}
            <div className="form-actions">
              <button type="button" onClick={onCancel} className="btn btn-secondary" disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? 'Verifying...' : 'Verify & Enable'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AuthenticatorSetup;
